import { createElement, useCallback, useEffect, useMemo } from "react";

import { StyledModalFooter } from "app/pages/sales-intelligence/sub-modules/signals/components/modals/SignalsModal/styles";
import useEcommerceSignalFiltersViewResolver from "app/pages/sales-intelligence/sub-modules/signals/hooks/useEcommerceSignalFiltersViewResolver";
import useSignalFiltersState from "app/pages/sales-intelligence/sub-modules/signals/hooks/useFiltersState";
import type SignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalFilters";
import { coerceEcommerceFiltersForForm } from "app/pages/sales-intelligence/sub-modules/signals/utils/ecommerce/ecommerceFilterNormalization";
import type EcommerceSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceSignalFilters";
import type { EcommerceSignalSubType } from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceSignalSubType";
import { isEcommerceSignalFilters } from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/type-guards/is-ecommerce-signal-filters";
import type SignalTemplate from "app/pages/sales-intelligence/sub-modules/signals/models/templates/SignalTemplate";
import type SignalFiltersViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersViewProps";

import { StyledFiltersViewContent, StyledFiltersViewInner, StyledNotificationWrapper } from "../../styles";

import EcommerceSignalFiltersViewHeader from "./EcommerceSignalFiltersViewHeader/EcommerceSignalFiltersViewHeader";

type Props = SignalFiltersViewProps<Partial<EcommerceSignalFilters>>;

const EcommerceSignalFiltersView = (props: Props) => {
    const normalizedInitial = useMemo(
        () => coerceEcommerceFiltersForForm(props.signalSubType ?? "", props.filters),
        [props.signalSubType, props.filters],
    );

    const { currentFilters, setCurrentFilters } = useSignalFiltersState(normalizedInitial, props.onFiltersChange);

    useEffect(() => {
        setCurrentFilters(normalizedInitial);
    }, [normalizedInitial, setCurrentFilters]);

    const filtersViewResolver = useEcommerceSignalFiltersViewResolver();

    const FiltersView = useMemo(() => {
        return filtersViewResolver.getFiltersView(props.signalSubType as EcommerceSignalSubType);
    }, [filtersViewResolver, props.signalSubType]);

    const handleTemplateChange = useCallback(
        (template: Readonly<SignalTemplate<SignalFilters>>): void => {
            setCurrentFilters(
                coerceEcommerceFiltersForForm(
                    template.signalSubType ?? props.signalSubType ?? "",
                    template.filters as Partial<EcommerceSignalFilters>,
                ),
            );
        },
        [props.signalSubType, setCurrentFilters],
    );

    function handleChange(updatedFilters: Partial<EcommerceSignalFilters>): void {
        setCurrentFilters((prevFilters) => ({ ...prevFilters, ...updatedFilters }));
    }

    function handleSubmit(): void {
        props.onFiltersSubmit(currentFilters);
    }

    return (
        <StyledFiltersViewInner alignItems="center">
            <StyledFiltersViewContent>
                <EcommerceSignalFiltersViewHeader
                    renderTemplateView={props.renderTemplateView}
                    onTemplateSelect={handleTemplateChange}
                />

                {/* FiltersView is resolved dynamically per sub-type and may be created fresh on
                    each resolution, so it can't be expressed as a static JSX tag; the useMemo
                    above keeps it stable. */}
                {FiltersView
                    ? createElement(FiltersView, {
                          key: props.signalAlias,
                          filters: currentFilters,
                          signalAlias: props.signalAlias,
                          signalSubType: props.signalSubType,
                          onFiltersChange: handleChange,
                      })
                    : null}

                {props.notificationView ? (
                    <StyledNotificationWrapper>{props.notificationView}</StyledNotificationWrapper>
                ) : null}
            </StyledFiltersViewContent>
            <StyledModalFooter>
                {props.renderSubmit({
                    isDisabled: !isEcommerceSignalFilters(currentFilters as SignalFilters, props.signalSubType),
                    onSubmit: handleSubmit,
                })}
            </StyledModalFooter>
        </StyledFiltersViewInner>
    );
};

export default EcommerceSignalFiltersView;
